import type { DevicePresence, GraphAnalysis, GraphEdge, GraphState, StoredEvent } from "@facade/dashboard-shared";

/** A 401 anywhere means the session is gone: send the user to the login page and come back here after. */
export function redirectToLogin() {
  if (location.pathname === "/login") return;
  const next = encodeURIComponent(location.pathname + location.search);
  location.assign(`/login?next=${next}`);
}

async function get<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "same-origin" });
  if (r.status === 401) {
    redirectToLogin();
    throw new Error("unauthorized");
  }
  if (!r.ok) throw new Error(`${r.status} ${r.statusText} — ${url}`);
  return (await r.json()) as T;
}

async function send<T>(method: string, url: string, body?: unknown, opts: { noRedirect?: boolean } = {}): Promise<T> {
  const r = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401 && !opts.noRedirect) {
    redirectToLogin();
    throw new Error("unauthorized");
  }
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try {
      const j = await r.json();
      if (j?.error) msg = `${r.status} ${j.error}`; // keep the status visible — "Not Found" alone hides what happened
      else if (j?.message) msg = `${r.status} ${j.message}`;
    } catch {
      /* no body */
    }
    throw new Error(msg);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export interface Run {
  run_id: string;
  device_id: string;
  parent_run_id: string | null;
  mode: string | null;
  prompt: string | null;
  source: string | null;
  started_ts: number | null;
  ended_ts: number | null;
  ok: number | null;
  result: string | null;
  error: string | null;
  model_calls: number;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number | null;
  variant_exp: string | null;
  variant_id: string | null;
  git_sha: string | null;
  application_id: string | null;
  children?: Run[];
}

export interface ModelQueryRow {
  event_id: number;
  device_id: string;
  run_id: string | null;
  ts: number;
  purpose: string | null;
  provider: string | null;
  model: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cache_read: number | null;
  cache_write: number | null;
  latency_ms: number | null;
  ok: number | null;
  error: string | null;
  cost_usd: number | null;
  variant_exp: string | null;
  variant_id: string | null;
  git_sha: string | null;
  application_id: string | null;
  nickname?: string;
  run_prompt?: string;
}

export interface LoopRow {
  device_id: string;
  loop_id: number;
  label: string | null;
  capability_key: string | null;
  installed_ts: number | null;
  killed_ts: number | null;
  ticks: number;
  last_tick_ts: number | null;
  last_ok: number | null;
}

export interface DeviceDetail extends DevicePresence {
  stats: { events: number; runs: number; tokens_in_today: number; tokens_out_today: number; events_last_minute: number };
}


/** A human's account of a run they issued. The prose fields are FOR HUMANS — never model input. */
export interface FeedbackEntry {
  id: number;
  run_id: string;
  device_id: string | null;
  author: string;
  intention: string;
  expectation: string | null;
  what_happened: string | null;
  what_went_wrong: string | null;
  promoted_to_request: number | null;
  created_ts: number;
  handling: { id: number; author: string; body: string; created_ts: number }[];
  attachments: { id: number; mime: string; bytes: number; created_ts: number }[];
  /** Closed vocabulary, chosen not written — the one part of an entry that may be scored. */
  verdicts: string[];
  /** When the verdict was last set; a large gap from created_ts means it was discovered late. */
  verdict_ts: number | null;
}

export interface NewFeedback {
  runId: string;
  deviceId?: string | null;
  author: string;
  intention: string;
  expectation?: string;
  whatHappened?: string;
  whatWentWrong?: string;
  verdicts?: string[];
}

export interface BuildInfo {
  release_name: string | null;
  description: string | null;
  changes: string | null;
  testing: string | null;
  known_issues: string | null;
  updated_ts: number;
}

export interface Build {
  id: string;
  filename: string;
  version_name: string | null;
  version_code: number | null;
  commit_sha: string | null;
  branch: string | null;
  notes: string | null;
  bytes: number | null;
  uploaded_ts: number;
  /** When the artifact was BUILT; null when the publisher did not report it. Never the upload time. */
  built_ts: number | null;
  variant_exp: string | null;
  variant_id: string | null;
  newest: boolean;
  /** Null until a person writes it. A build with no notes is normal, not broken. */
  info: BuildInfo | null;
  devices: { device_id: string; nickname: string; last_seen: number }[];
}

export const api = {
  builds: () => get<Build[]>("/api/builds"),
  build: (id: string) => get<Build>(`/api/builds/${encodeURIComponent(id)}`),
  saveBuildInfo: (id: string, body: Partial<Record<"releaseName" | "description" | "changes" | "testing" | "knownIssues", string>>) =>
    send<BuildInfo>("PUT", `/api/builds/${encodeURIComponent(id)}/info`, body),
  feedback: (runId?: string) => get<FeedbackEntry[]>(`/api/feedback${runId ? `?runId=${encodeURIComponent(runId)}` : ""}`),
  addFeedback: (body: NewFeedback) => send<FeedbackEntry>("POST", "/api/feedback", body),
  addHandling: (id: number, body: { author: string; body: string }) =>
    send<FeedbackEntry>("POST", `/api/feedback/${id}/handling`, body),
  /** Attach a pasted or chosen screenshot. Raw bytes, typed by the blob's own mime. */
  setVerdicts: (id: number, verdicts: string[]) =>
    send<FeedbackEntry>("PATCH", `/api/feedback/${id}`, { verdicts }),
  addAttachment: async (id: number, file: Blob): Promise<FeedbackEntry> => {
    const r = await fetch(`/api/feedback/${id}/attachment`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": file.type },
      body: file,
    });
    if (r.status === 401) {
      redirectToLogin();
      throw new Error("unauthorized");
    }
    if (!r.ok) throw new Error(`${r.status} ${r.statusText} — attachment`);
    return (await r.json()) as FeedbackEntry;
  },
  me: () => get<{ authenticated: boolean; user: string | null; authDisabled: boolean }>("/api/me"),
  login: (password: string) => send<void>("POST", "/api/login", { password }, { noRedirect: true }),
  logout: () => send<void>("POST", "/api/logout"),
  connectInfo: (url?: string) =>
    get<ConnectInfo>(`/api/connect-info${url ? `?url=${encodeURIComponent(url)}` : ""}`),
  enrollToken: () => send<{ token: string; expiresAt: number; wsUrl: string; qr: string }>("POST", "/api/enroll-tokens"),
  revokeDevice: (id: string) => send<{ ok: boolean }>("DELETE", `/api/devices/${encodeURIComponent(id)}/secret`),
  devices: () => get<DevicePresence[]>("/api/devices"),
  device: (id: string) => get<DeviceDetail>(`/api/devices/${encodeURIComponent(id)}`),
  runs: (id: string, limit = 50, before?: number) =>
    get<Run[]>(`/api/devices/${encodeURIComponent(id)}/runs?limit=${limit}${before ? `&before=${before}` : ""}`),
  run: (runId: string) => get<Run>(`/api/runs/${encodeURIComponent(runId)}`),
  runEvents: (runId: string) => get<StoredEvent[]>(`/api/runs/${encodeURIComponent(runId)}/events`),
  events: (id: string, p: { kinds?: string[]; excludeKinds?: string[]; runId?: string; search?: string; cursor?: number | null; limit?: number }) => {
    const qs = new URLSearchParams();
    if (p.kinds?.length) qs.set("kinds", p.kinds.join(","));
    if (p.excludeKinds?.length) qs.set("excludeKinds", p.excludeKinds.join(","));
    if (p.runId) qs.set("runId", p.runId);
    if (p.search) qs.set("search", p.search);
    if (p.cursor) qs.set("cursor", String(p.cursor));
    if (p.limit) qs.set("limit", String(p.limit));
    return get<{ events: StoredEvent[]; nextCursor: number | null }>(`/api/devices/${encodeURIComponent(id)}/events?${qs}`);
  },
  event: (id: number) => get<StoredEvent>(`/api/events/${id}`),
  modelQueries: (p: { deviceId?: string; runId?: string; limit?: number; cursor?: number | null }) => {
    const qs = new URLSearchParams();
    if (p.deviceId) qs.set("deviceId", p.deviceId);
    if (p.runId) qs.set("runId", p.runId);
    if (p.limit) qs.set("limit", String(p.limit));
    if (p.cursor) qs.set("cursor", String(p.cursor));
    return get<ModelQueryRow[]>(`/api/model-queries?${qs}`);
  },
  modelQuery: (eventId: number) => get<{ meta: ModelQueryRow | null; event: StoredEvent }>(`/api/model-queries/${eventId}`),
  graph: (id: string) => get<{ states: GraphState[]; edges: GraphEdge[]; analysis: GraphAnalysis }>(`/api/devices/${encodeURIComponent(id)}/graph`),
  loops: (id: string) => get<LoopRow[]>(`/api/devices/${encodeURIComponent(id)}/loops`),
  feeds: (id: string) => get<{ latest: Record<string, { value: unknown; ts: number }>; history: StoredEvent[] }>(`/api/devices/${encodeURIComponent(id)}/feeds`),
  blobUrl: (sha: string) => `/api/blobs/${sha}`,
  blobJson: <T = unknown>(sha: string) => get<T>(`/api/blobs/${sha}`),
  apks: () => get<ApkRow[]>("/api/apks"),
  publishers: () => get<string[]>("/api/publishers"),
  variants: () => get<{ registry: VariantsRegistry | null; updated_ts: number | null }>("/api/variants"),
  costs: (days = 30) => get<CostsResponse>(`/api/costs?days=${days}`),
  modelsCatalog: () => get<Catalog>("/api/models"),
  modelsPut: (providers: Catalog["providers"]) => send<Catalog>("PUT", "/api/models", { providers }),
  requests: () => get<RequestRow[]>("/api/requests"),
  requestCreate: (r: { title: string; note?: string }) => send<RequestRow>("POST", "/api/requests", r),
  requestUpdate: (id: number, patch: Partial<Pick<RequestRow, "title" | "note" | "status" | "branch" | "commit_sha" | "claimant">>) =>
    send<RequestRow>("PATCH", `/api/requests/${id}`, patch),
  tasks: () => get<TaskRow[]>("/api/tasks"),
  taskCreate: (t: { title: string; note?: string; lane?: string }) => send<TaskRow>("POST", "/api/tasks", t),
  taskUpdate: (id: number, patch: Partial<Pick<TaskRow, "title" | "note" | "lane">> & { before?: number | null; after?: number | null }) =>
    send<TaskRow>("PATCH", `/api/tasks/${id}`, patch),
  taskDelete: (id: number) => send<{ ok: boolean }>("DELETE", `/api/tasks/${id}`),
};

/** tools/variants/variants.json — stored verbatim on the server; loose typing on purpose. */
export interface VariantOption {
  variant: string;
  branch?: string;
  summary?: string;
  tip?: string;
  value?: string;
  default?: boolean;
  switch?: string;
  [k: string]: unknown;
}
export interface VariantExperiment {
  id: string;
  question: string;
  kind: "branch" | "toggle";
  options: VariantOption[];
  status?: string;
  decision?: string | null;
  board_card?: number;
  baseline?: string;
  toggle?: { mechanism?: string; action?: string; extra?: string; default?: string; note?: string; [k: string]: unknown };
  [k: string]: unknown;
}
export interface VariantsRegistry {
  experiments: VariantExperiment[];
  [k: string]: unknown;
}

export interface CostBucket {
  key: string;
  calls: number;
  tokens_in: number;
  tokens_out: number;
  cache_read: number;
  cache_write: number;
  cost_usd: number | null;
  nickname?: string | null;
}
export interface CostsResponse {
  days: number;
  totals: CostBucket;
  perDay: CostBucket[];
  perModel: CostBucket[];
  perPurpose: CostBucket[];
  perDevice: CostBucket[];
  /** Variant traffic only, keyed "<exp>/<variant>" (docs/variants.md); empty without experiments. */
  perVariant: CostBucket[];
  unpriced: string[];
  pricedModels: string[];
}
export interface CatalogModel {
  id: string;
  label: string;
  vision?: boolean;
  price?: { input: number; output: number; cacheRead?: number; cacheWrite?: number };
}
export interface Catalog {
  v: 1;
  updated_ts: number;
  source: "default" | "file";
  providers: { id: string; label: string; baseUrl: string; models: CatalogModel[] }[];
}

export type RequestStatus = "open" | "claimed" | "doing" | "verifying" | "done" | "failed";
export interface RequestRow {
  id: number;
  title: string;
  note: string | null;
  status: RequestStatus;
  claimant: string | null;
  branch: string | null;
  commit_sha: string | null;
  verdict: string | null;
  verdict_ts: number | null;
  created_ts: number;
  updated_ts: number;
}

export type Lane = "idea" | "todo" | "doing" | "done";
export interface TaskRow {
  id: number;
  title: string;
  note: string | null;
  lane: Lane;
  pos: number;
  created_ts: number;
  updated_ts: number;
}

export interface ConnectInfo {
  wsUrls: string[];
  emulatorUrl: string;
  primary: string;
  qrSvg: string;
  token: string;
  expiresAt: number;
  authDisabled: boolean;
}

export interface ApkRow {
  id: string;
  filename: string;
  application_id: string | null;
  version_name: string | null;
  version_code: number | null;
  variant: string | null;
  commit_sha: string | null;
  branch: string | null;
  notes: string | null;
  sha256: string;
  bytes: number;
  uploaded_ts: number;
  /** When the artifact was BUILT; null when the publisher did not report it. Never the upload time. */
  built_ts: number | null;
  publisher: string | null;
  signature: string | null;
  variant_exp: string | null;
  variant_id: string | null;
  url: string;
}
